import {createAction} from 'redux-actions'
import {getEditableCustomerIds, getEditableProjectIds, getFilterCPSelection, getSingleCPSelection, getTaskStatusFilter, getViewMode,} from "./selectors"
import {addRecentlyCreatedTasks as addRecentlyCreatedTasksToKanban, setLoaded as setKanbanLoaded} from "../kanban/actions"
import {addRecentlyCreatedTasks as addRecentlyCreatedTasksToTable} from '../tasksTable/actions'
import {setLoaded as setTaskTableLoaded} from '../tasksTable/actions'
import * as EntitiesActions from "../../entities/actions"
import {getCustomer, getCustomers, getProject, getProjects} from "../../entities/selectors"
import * as ActionTypes from "./actionTypes"
import {isMinimizedCards} from "../kanban/selectors"
import rpcCall from "../../util/rpc/rpcCalls"
import * as SyncCallbacks from "../syncCallbacks"
import {createActionObject} from "../../redux/utils"
import {
    createIntersectedCPSelection,
    removeCustomerFromCPSelection,
    removeProjectFromCPSelection,
    saveSingleCPSelectionToLocalStorage,
    setCpPanelClosedStateInLocalStorage
} from './utils'
import {getColumnsOrder, getShownColumns, getTasksSorting} from '../tasksTable/selectors'
import {arrayToMap, arrayToSet} from "../../atUtils"
import messageResource from "../../messageResource"
import CustomerProjectSelection from '../../CustomerProjectSelection'
import {getFeaturesChangesMessage} from '../../features/utils'
import {batched} from '../../redux/batch'
import {
    CUSTOMER_CANNOT_BE_MODIFIED_EXCEPTION,
    CUSTOMER_UNAVAILABLE_EXCEPTION,
    NO_SUCH_TASK_EXCEPTION, PROJECT_CANNOT_BE_MODIFIED_EXCEPTION, PROJECT_IS_NOT_AVAILABLE_EXCEPTION, TASK_CANNOT_BE_MODIFIED_EXCEPTION,
    TASK_UNAVAILABLE_EXCEPTION
} from './ExceptionsTypes'
import {TaskManagementViewMode} from '../constants'
import {addProject} from '../../entities/actions'

const setViewMode = createAction(ActionTypes.VIEW_MODE_SET)

export const toggleLoadingMoire = createAction(ActionTypes.TOGGLE_LOADING_MOIRE)

export const addRecentlyCreatedTasks = (taskIds) => batched(dispatch => {
    dispatch(createActionObject(ActionTypes.RECENTLY_CREATED_TASKS_ADD, taskIds))
    dispatch(addRecentlyCreatedTasksToKanban(taskIds))
    dispatch(addRecentlyCreatedTasksToTable(taskIds))
})

export const setLastCreatedTaskIds = createAction(ActionTypes.LAST_CREATED_TASK_IDS_SET)
export const removeLastCreatedTaskIds = createAction(ActionTypes.LAST_CREATED_TASK_IDS_REMOVE)

export const setShowOnlyManageableTasks = (onlyManageable) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.SHOW_ONLY_MANAGEABLE_TASKS_SET, onlyManageable))
    dispatch(reloadTasks())
    dispatch(savePageSettings())
}

export const switchViewMode = viewMode => (dispatch) => {
    dispatch(setViewMode(viewMode))
    dispatch(savePageSettings())
}

export const removeLastCreatedTaskIdsAction = (id) => {
    return (dispatch) => dispatch(removeLastCreatedTaskIds(id))
}

export const changeSingleCPSelectionAction = (selection, skipReload = false) => (dispatch) => {
    dispatch(changeSingleCPSelection(selection))
    dispatch(createActionObject(ActionTypes.SHOW_ONLY_MANAGEABLE_TASKS_SET, false))
    if (!skipReload) {
        dispatch(reloadTasks())
    }
}

export const changeFilterCPSelectionAction = (selection, skipReload = false) => (dispatch, getState) => {
    const state = getState()
    const filterCPSelection = CustomerProjectSelection.fromPlainObject(selection)
    const prevFilterCPSelection = CustomerProjectSelection.fromPlainObject(getFilterCPSelection(state))

    if (!filterCPSelection.isEmpty() && !filterCPSelection.equals(prevFilterCPSelection)) {
        dispatch(createActionObject(ActionTypes.RECENTLY_CREATED_CUSTOMERS_PROJECTS_CLEAR))
        const intersectedCpSelection = createIntersectedCPSelection(
            selection,
            getSingleCPSelection(state),
            getCustomers(state),
            getProjects(state),
        )

        if (intersectedCpSelection.isEmpty()) {
            dispatch(changeSingleCPSelection({customerId: null, projectId: null}))
        }

        dispatch(createActionObject(ActionTypes.FILTER_CP_SELECTION_CHANGE, filterCPSelection.toPlainObject()))
        dispatch(createActionObject(ActionTypes.SHOW_ONLY_MANAGEABLE_TASKS_SET, false))
        if (!skipReload) {
            dispatch(reloadTasks())
        }
        dispatch(savePageSettings())
    }
}

//TODO remove this action after correct tasks reloading on intersected CP selection change. See https://github.com/Actimind-Development/actitime/issues/1428
export const changeCPSelection = (singleCPSelection, filteredCPSelection) => (dispatch) => {
    // skip task loading fix https://github.com/Actimind-Development/actitime/pull/1427 (without batch)
    // batch is not possible to use cause `savePageSettings` called with old state
    dispatch(changeFilterCPSelectionAction(filteredCPSelection, true))
    dispatch(changeSingleCPSelectionAction(singleCPSelection, true))
    dispatch(reloadTasks())
}

export const changeCustomerProjectNameFilter = (text) => dispatch => {
    dispatch(createActionObject(ActionTypes.RECENTLY_CREATED_CUSTOMERS_PROJECTS_CLEAR))
    dispatch(createActionObject(ActionTypes.CP_NAME_FILTER_SET, text))
}

export const applyTaskStatusFilterAction = (filter) => dispatch => {
    dispatch(createAction(ActionTypes.TASK_STATUS_FILTER_CHANGE)(filter))
    dispatch(reloadTasks())
    dispatch(savePageSettings())
    SyncCallbacks.afterChangeStatusFilterSelection(filter)
}
export const changeTaskNameFilterAction = (filter) => dispatch => {
    dispatch(createAction(ActionTypes.TASK_NAME_FILTER_CHANGE)(filter))
    dispatch(reloadTasks())
}

export const addNewTasks = ({
    tasks,
    projects,
    customers
}) => dispatch => {
    dispatch(EntitiesActions.addTasks(tasks))
    dispatch(EntitiesActions.addProjects(projects))
    dispatch(EntitiesActions.addCustomers(customers))

    const taskIds = tasks.map(task => task.id)
    dispatch(setLastCreatedTaskIds(taskIds))
    dispatch(addRecentlyCreatedTasks(taskIds))
}

export const setRecentlyCreatedCustomers = (customerIds) => dispatch => {
    dispatch(createActionObject(ActionTypes.RECENTLY_CREATED_CUSTOMER_IDS_SET, customerIds))
}

export const setRecentlyCreatedProjects = (projectIds) => dispatch => {
    dispatch(createActionObject(ActionTypes.RECENTLY_CREATED_PROJECT_IDS_SET, projectIds))
}

export const setManagementAssignments = (managementAssignments) => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.MANAGEMENT_ASSIGNMENTS_SET, {
        customers: arrayToMap(managementAssignments['customers']),
        projects: arrayToMap(managementAssignments['projects']),
        tasks: arrayToMap(managementAssignments['tasks'])
    }))

    //TODO temporary for TaskPanelController, remove after connect to redux
    SyncCallbacks.setEditableProjectIds(getEditableProjectIds(getState()))
    SyncCallbacks.setEditableCustomerIds(getEditableCustomerIds(getState()))
}

export const addTasks = (tasks) => dispatch => {
    dispatch(EntitiesActions.addTasks(tasks))
    SyncCallbacks.addTasks(tasks)
}

export const removeTasks = (taskIds) => dispatch => {
    dispatch(EntitiesActions.removeTasks(taskIds))
    SyncCallbacks.removeTasks(taskIds)
}

export const removeProject = (projectId) => (dispatch, getState) => {
    const state = getState()
    const filterCPSelection = removeProjectFromCPSelection(projectId, getFilterCPSelection(state))
    const singleCPSelection = getSingleCPSelection(state)
    dispatch(changeFilterCPSelectionAction(filterCPSelection))
    if (singleCPSelection.projectId === projectId) {
        dispatch(changeSingleCPSelectionAction({customerId: null, projectId: null}))
    }

    const recentlyCreatedProjectIds = state.taskManagement.common.recentlyCreatedProjectIds
    dispatch(setRecentlyCreatedProjects(recentlyCreatedProjectIds.filter(id => id !== projectId)))

    dispatch(EntitiesActions.removeProject(projectId))
}

export const removeCustomer = (customerId) => (dispatch, getState) => {
    const state = getState()
    const projects = getProjects(state)
    const singleCPSelection = getSingleCPSelection(state)
    const filterCPSelection = removeCustomerFromCPSelection(getFilterCPSelection(state), customerId, projects)
    dispatch(changeFilterCPSelectionAction(filterCPSelection))
    if (singleCPSelection.customerId === customerId) {
        dispatch(changeSingleCPSelectionAction({customerId: null, projectId: null}))
    }
    const projectsOfCustomerIds = Object.values(projects)
                                        .filter(project => project.customerId === customerId)
                                        .map(p => p.id)

    dispatch(EntitiesActions.removeProjects(projectsOfCustomerIds))

    const recentlyCreatedCustomerIds = state.taskManagement.common.recentlyCreatedCustomerIds
    dispatch(setRecentlyCreatedCustomers(recentlyCreatedCustomerIds.filter(id => id !== customerId)))

    const recentlyCreatedProjectIds = state.taskManagement.common.recentlyCreatedProjectIds
    const projectsOfCustomerIdsSet = arrayToSet(projectsOfCustomerIds)
    dispatch(setRecentlyCreatedProjects(recentlyCreatedProjectIds.filter(id => !projectsOfCustomerIdsSet[id])))

    dispatch(EntitiesActions.removeCustomer(customerId))
}

export const archiveProjectsOfCustomerAndReloadTasks = (customerId) => (dispatch, getState) => {
    const state = getState()
    const projects = Object.values(getProjects(state)).filter(p => p.customerId === customerId && !p.archived)
    projects.forEach(p => dispatch(archiveProject(p.id)))
    dispatch(reloadTasks())
}

export const archiveProjectAndReloadTasks = (projectId) => (dispatch) => {
    dispatch(archiveProject(projectId))
    dispatch(reloadTasks())
}

export const updateProject = (project) => (dispatch, getState) => {
    if (!project.archived && getProject(getState(), project.id).archived) {
        dispatch(unarchiveCustomer(project.customerId))
    }
    dispatch(addProject(project))
}

const unarchiveCustomer = customerId => (dispatch, getState) => {
    const customer = getCustomer(getState(), customerId)
    dispatch(EntitiesActions.addCustomers([{...customer, archived: false}]))
}

const archiveProject = (projectId) => (dispatch, getState) => {
    const state = getState()
    const project = getProject(state, projectId)
    dispatch(EntitiesActions.addProjects([{...project, archived: true}]))
}

export const setViewerSettings = (viewerSettings) => dispatch => {
    dispatch(createActionObject(ActionTypes.VIEWER_SETTINGS_SET, viewerSettings))
}

export const setHasManageableTasks = (hasManageableTasks) => dispatch => {
    dispatch(createActionObject(ActionTypes.HAS_MANAGEABLE_TASKS_SET, hasManageableTasks))
}

export const savePageSettings = () => (dispatch, getState) => {
    const state = getState()
    const {by, asc} = getTasksSorting(state)
    const shownColumns = getShownColumns(state)
    rpcCall(
        "TaskManagementService.savePageSettings",
        [
            getFilterCPSelection(state),
            by,
            asc,
            getTaskStatusFilter(state),
            isMinimizedCards(state),
            getViewMode(state),
            getColumnsOrder(state),
            shownColumns,
        ],
        () => true
    ).catch(e => console.error(e))
}

export const setBulkOperationsPanelShownState = (shown) => (dispatch) => {
    dispatch(createActionObject(ActionTypes.BULK_OPERATIONS_PANEL_SHOWN_SET, shown))
}

export const handleCommonTaskUpdateErrors = (task, exception, dispatch) => {
    switch (exception) {
        case NO_SUCH_TASK_EXCEPTION:
            dispatch(removeTasks([task.id]))
            alert(messageResource.getMessage("task.error.concurrently_deleted.cannot_edit"))
            return true
        case TASK_UNAVAILABLE_EXCEPTION:
            if (task) {
                dispatch(removeTasks([task.id]))
            }
            alert(messageResource.getMessage("task.list.error.update_task.task_unavailable"))
            return true
        case TASK_CANNOT_BE_MODIFIED_EXCEPTION:
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
            window.location.reload()
            return true
        case PROJECT_IS_NOT_AVAILABLE_EXCEPTION:
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
            dispatch(removeProject(task.projectId))
            dispatch(reloadTasks())
            return true
        case PROJECT_CANNOT_BE_MODIFIED_EXCEPTION:
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
            dispatch(reloadTasks())
            return true
        case CUSTOMER_UNAVAILABLE_EXCEPTION:
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
            dispatch(removeCustomer(task.customerId))
            dispatch(reloadTasks())
            return true
        case CUSTOMER_CANNOT_BE_MODIFIED_EXCEPTION:
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"))
            dispatch(reloadTasks())
            return true
    }
    return false
}

export const reloadTasks = () => (dispatch, getState) => {
    if (TaskManagementViewMode.TABLE === getViewMode(getState())) {
        dispatch(setTaskTableLoaded(true))
        dispatch(setTaskTableLoaded(false))
    } else {
        dispatch(setKanbanLoaded(true))
        dispatch(setKanbanLoaded(false))
    }
}

export const expandPanel = () => (dispatch) => {
    setCpPanelClosedStateInLocalStorage(false)
    dispatch(createActionObject(ActionTypes.CP_PANEL_CLOSED_STATE_CHANGE, false))
}

export const collapsePanel = () => (dispatch) => {
    setCpPanelClosedStateInLocalStorage(true)
    dispatch(createActionObject(ActionTypes.CP_PANEL_CLOSED_STATE_CHANGE, true))
}

export const handleFeatureChange = (changedFeatures) => () => {
    const message = getFeaturesChangesMessage(changedFeatures).concat(messageResource.getMessage('common.interface_will_updated'))
    alert(message)
}

const changeSingleCPSelection = (selection) => (dispatch) => {
    saveSingleCPSelectionToLocalStorage(selection)
    dispatch(createActionObject(ActionTypes.SINGLE_CP_SELECTION_CHANGE, selection))
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/common/actions.js